// Recurrence utility functions for calculating recurring tournament dates

/**
 * Day of week constants
 */
export const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' }
];

/**
 * Nth weekday options (for monthly recurrence)
 */
export const NTH_OPTIONS = [
  { value: 1, label: '1st' },
  { value: 2, label: '2nd' },
  { value: 3, label: '3rd' },
  { value: 4, label: '4th' },
  { value: -1, label: 'Last' }
];

/**
 * Check if a date matches a cancelled date
 * @param {Object} eventData - Event configuration
 * @param {Date} date - Date to check
 * @returns {boolean} - True if date is cancelled
 */
export const isOccurrenceCancelled = (eventData, date) => {
  if (!eventData.cancelledDates || eventData.cancelledDates.length === 0) {
    return false;
  }

  const dateStr = formatDateToString(date);
  return eventData.cancelledDates.includes(dateStr);
};

/**
 * Get actual date after applying rescheduled dates
 * @param {Object} eventData - Event configuration
 * @param {Date} calculatedDate - Original calculated date
 * @returns {Date} - Actual date (rescheduled if applicable)
 */
export const getActualDate = (eventData, calculatedDate) => {
  if (!eventData.rescheduledDates || eventData.rescheduledDates.length === 0) {
    return calculatedDate;
  }

  const dateStr = formatDateToString(calculatedDate);
  const reschedule = eventData.rescheduledDates.find(r => r.originalDate === dateStr);

  if (reschedule && reschedule.newDate) {
    return new Date(reschedule.newDate);
  }

  return calculatedDate;
};

/**
 * Format date to YYYY-MM-DD string
 * @param {Date} date - Date object
 * @returns {string} - Formatted date string
 */
export const formatDateToString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Calculate next occurrence of a weekly event
 * @param {Date} fromDate - Starting date for calculation
 * @param {number} dayOfWeek - Target day of week (0-6)
 * @returns {Date} - Next occurrence
 */
const calculateNextWeekly = (fromDate, dayOfWeek) => {
  const result = new Date(fromDate);
  const currentDay = result.getDay();

  // Calculate days until target day
  let daysUntilTarget = dayOfWeek - currentDay;

  // If target day is in the past this week (but not today), go to next week
  // This gives +1 day grace period - if today is the tournament day, include it
  if (daysUntilTarget < 0) {
    daysUntilTarget += 7;
  }

  result.setDate(result.getDate() + daysUntilTarget);
  return result;
};

/**
 * Calculate next occurrence of a monthly event (day of month pattern)
 * @param {Date} fromDate - Starting date for calculation
 * @param {number} dayOfMonth - Target day of month (1-31)
 * @returns {Date} - Next occurrence
 */
const calculateNextMonthlyDay = (fromDate, dayOfMonth) => {
  const result = new Date(fromDate);

  // Try current month first
  const daysInCurrentMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();

  // Include current day (+1 day grace period)
  if (dayOfMonth <= daysInCurrentMonth && result.getDate() <= dayOfMonth) {
    // Target day exists in current month and we haven't passed it (or it's today)
    result.setDate(dayOfMonth);
    return result;
  }

  // Move to next month
  result.setMonth(result.getMonth() + 1);
  result.setDate(1);

  // Check if target day exists in next month (handle Feb 29, 30, 31)
  const daysInNextMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();

  if (dayOfMonth <= daysInNextMonth) {
    result.setDate(dayOfMonth);
  } else {
    // Day doesn't exist in this month (e.g., Feb 30), skip to next month
    return calculateNextMonthlyDay(result, dayOfMonth);
  }

  return result;
};

/**
 * Calculate next occurrence of a monthly event (nth weekday pattern)
 * @param {Date} fromDate - Starting date for calculation
 * @param {number} nth - Nth occurrence (1-4, -1 for last)
 * @param {number} dayOfWeek - Target day of week (0-6)
 * @returns {Date} - Next occurrence
 */
const calculateNextMonthlyNthWeekday = (fromDate, nth, dayOfWeek) => {
  const findNthWeekday = (year, month, nth, dayOfWeek) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    if (nth === -1) {
      // Last occurrence of the weekday in the month
      let date = new Date(lastDay);
      while (date.getDay() !== dayOfWeek) {
        date.setDate(date.getDate() - 1);
      }
      return date;
    } else {
      // Nth occurrence (1st, 2nd, 3rd, 4th)
      let date = new Date(firstDay);
      let count = 0;

      while (date.getMonth() === month) {
        if (date.getDay() === dayOfWeek) {
          count++;
          if (count === nth) {
            return date;
          }
        }
        date.setDate(date.getDate() + 1);
      }

      // Nth occurrence doesn't exist in this month
      return null;
    }
  };

  // Try current month - include current day (+1 day grace period)
  const currentMonthDate = findNthWeekday(fromDate.getFullYear(), fromDate.getMonth(), nth, dayOfWeek);

  if (currentMonthDate && currentMonthDate >= fromDate) {
    return currentMonthDate;
  }

  // Try next months until we find one with the nth occurrence
  let testDate = new Date(fromDate);
  testDate.setMonth(testDate.getMonth() + 1);
  testDate.setDate(1);

  for (let i = 0; i < 12; i++) {
    // Safety limit: check up to 12 months ahead
    const nextDate = findNthWeekday(testDate.getFullYear(), testDate.getMonth(), nth, dayOfWeek);

    if (nextDate) {
      return nextDate;
    }

    testDate.setMonth(testDate.getMonth() + 1);
  }

  // Fallback: return 1 month from now
  return new Date(fromDate.getFullYear(), fromDate.getMonth() + 1, 1);
};

/**
 * Calculate next occurrence of a recurring event
 * @param {Object} eventData - Event configuration
 * @param {Date} fromDate - Starting date for calculation (default: now)
 * @returns {Date|null} - Next occurrence date, or null if no more occurrences
 */
export const calculateNextOccurrence = (eventData, fromDate = new Date()) => {
  // Not a recurring event
  if (!eventData.isRecurring) {
    return eventData.startDate ? new Date(eventData.startDate) : null;
  }

  // No start date configured
  if (!eventData.recurrenceStartDate) {
    return null;
  }

  const startDate = new Date(eventData.recurrenceStartDate);
  const searchDate = fromDate < startDate ? startDate : fromDate;

  // Check if we've passed the end date
  if (eventData.recurrenceEndDate) {
    const endDate = new Date(eventData.recurrenceEndDate);
    if (searchDate > endDate) {
      return null; // No more occurrences
    }
  }

  // Calculate next occurrence based on recurrence type
  let nextDate;

  if (eventData.recurrenceType === 'weekly') {
    nextDate = calculateNextWeekly(searchDate, eventData.weeklyDayOfWeek || 1);
  } else if (eventData.recurrenceType === 'monthly') {
    if (eventData.monthlyPattern === 'dayOfMonth') {
      nextDate = calculateNextMonthlyDay(searchDate, eventData.monthlyDayOfMonth || 1);
    } else {
      // nthWeekday pattern
      const nth = eventData.monthlyNthWeekday?.nth || 1;
      const dayOfWeek = eventData.monthlyNthWeekday?.dayOfWeek || 1;
      nextDate = calculateNextMonthlyNthWeekday(searchDate, nth, dayOfWeek);
    }
  } else {
    return null; // Invalid recurrence type
  }

  // Check if calculated date exceeds end date
  if (eventData.recurrenceEndDate) {
    const endDate = new Date(eventData.recurrenceEndDate);
    if (nextDate > endDate) {
      return null;
    }
  }

  // Skip cancelled dates
  if (isOccurrenceCancelled(eventData, nextDate)) {
    // Recursively find next non-cancelled occurrence
    const dayAfter = new Date(nextDate);
    dayAfter.setDate(dayAfter.getDate() + 1);
    return calculateNextOccurrence(eventData, dayAfter);
  }

  // Apply rescheduled dates
  nextDate = getActualDate(eventData, nextDate);

  return nextDate;
};

/**
 * Calculate multiple upcoming occurrences
 * @param {Object} eventData - Event configuration
 * @param {number} count - Number of occurrences to return
 * @param {Date} fromDate - Starting date for calculation (default: now)
 * @returns {Array<Date>} - Array of upcoming occurrence dates
 */
export const calculateUpcomingOccurrences = (eventData, count = 5, fromDate = new Date()) => {
  const occurrences = [];
  let searchDate = new Date(fromDate);

  for (let i = 0; i < count; i++) {
    const nextDate = calculateNextOccurrence(eventData, searchDate);

    if (!nextDate) {
      break; // No more occurrences
    }

    occurrences.push(nextDate);

    // Move search date to day after this occurrence
    searchDate = new Date(nextDate);
    searchDate.setDate(searchDate.getDate() + 1);
  }

  return occurrences;
};

/**
 * Format recurrence pattern as human-readable string
 * @param {Object} eventData - Event configuration
 * @returns {string} - Human-readable recurrence description
 */
export const formatRecurrencePattern = (eventData) => {
  if (!eventData.isRecurring) {
    return '';
  }

  if (eventData.recurrenceType === 'weekly') {
    const dayOfWeek = DAYS_OF_WEEK.find(d => d.value === eventData.weeklyDayOfWeek);
    return `Every ${dayOfWeek?.label || 'week'}`;
  } else if (eventData.recurrenceType === 'monthly') {
    if (eventData.monthlyPattern === 'dayOfMonth') {
      const day = eventData.monthlyDayOfMonth || 1;
      const suffix = getDaySuffix(day);
      return `Every ${day}${suffix} of the month`;
    } else {
      const nthOption = NTH_OPTIONS.find(n => n.value === eventData.monthlyNthWeekday?.nth);
      const dayOfWeek = DAYS_OF_WEEK.find(d => d.value === eventData.monthlyNthWeekday?.dayOfWeek);
      return `${nthOption?.label || 'First'} ${dayOfWeek?.label || 'Monday'} of each month`;
    }
  }

  return 'Recurring event';
};

/**
 * Get ordinal suffix for day number (st, nd, rd, th)
 * @param {number} day - Day number
 * @returns {string} - Ordinal suffix
 */
const getDaySuffix = (day) => {
  if (day >= 11 && day <= 13) {
    return 'th';
  }
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

/**
 * Check if event is a recurring event
 * @param {Object} eventData - Event configuration
 * @returns {boolean} - True if recurring
 */
export const isRecurringEvent = (eventData) => {
  return eventData?.isRecurring === true;
};

/**
 * Format date for display
 * @param {Date} date - Date object
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted date string
 */
export const formatOccurrenceDate = (date, options = {}) => {
  const defaultOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  };

  return date.toLocaleDateString('en-US', defaultOptions);
};
